import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  Mail,
  Plus,
  Save,
  Settings2,
  Trash2,
  Upload,
  Users,
  X,
  ShieldCheck,
  Phone,
} from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';

import { Badge, Button, EmptyState, Input, Spinner } from '@terra/ui';

import { FormBanner, FormSelect } from '../components/form';
import { api } from '../lib/api';

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED SETTINGS MUTATION HELPER
// ═══════════════════════════════════════════════════════════════════════════════

function useSettingMutation(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: any) => api.cms.upsertSetting(key, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['website-public-setting', key] });
      void queryClient.invalidateQueries({ queryKey: ['website-setting', key] });
      // The "what is live" panel reads the same rows.
      void queryClient.invalidateQueries({ queryKey: ['effective-config'] });
    },
  });
}

/**
 * "Test connection": asks the provider whether the credentials now in use
 * authenticate. Run it after saving — the live site uses a saved key on its
 * very next request, so a wrong one is best found here.
 */
function IntegrationTestButton({
  name,
}: {
  name: 'razorpay' | 'shiprocket' | 'razorpayx' | 'google-maps';
}) {
  const test = useMutation({ mutationFn: () => api.cms.testIntegration(name) });
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => test.mutate()}
        disabled={test.isPending}
        className="border-border hover:bg-surface-subtle flex h-9 items-center gap-2 rounded-xl border px-4 text-[12.5px] font-bold disabled:opacity-50"
      >
        {test.isPending ? <Spinner size="sm" /> : <ShieldCheck className="h-4 w-4" />}
        Test connection
      </button>
      {test.data && (
        <span
          role="status"
          className={`text-[12.5px] font-semibold ${test.data.ok ? 'text-emerald-700' : 'text-red-700'}`}
        >
          {test.data.ok ? '✓ ' : '✕ '}
          {test.data.message}
          {test.data.mode && test.data.mode !== 'unknown' ? ` (${test.data.mode} mode)` : ''}
        </span>
      )}
      {test.isError && (
        <span className="text-[12.5px] font-semibold text-red-700">
          Could not run the check. Save first, then test.
        </span>
      )}
    </div>
  );
}

interface SettingField {
  name: string;
  label: string;
  /** Stored encrypted; reads back masked and is kept unless replaced. */
  secret?: boolean;
  placeholder?: string;
  hint?: string;
  options?: Array<{ value: string; label: string }>;
}

/**
 * One credentials card bound to one settings row. Secrets come back masked;
 * leaving a masked field alone keeps the stored value, typing replaces it, and
 * clearing it falls back to the server environment.
 */
function SettingCard({
  settingKey,
  title,
  description,
  fields,
  testName,
}: {
  settingKey: string;
  title: string;
  description: string;
  fields: SettingField[];
  testName?: 'razorpay' | 'shiprocket' | 'razorpayx';
}) {
  const {
    data: setting,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['website-setting', settingKey],
    queryFn: () => api.cms.getSetting(settingKey),
    retry: false,
  });
  const mutation = useSettingMutation(settingKey);
  const [form, setForm] = useState<Record<string, string>>({});
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (inited || isLoading) return;
    const stored = (setting?.value as Record<string, unknown> | null) ?? {};
    setForm(
      Object.fromEntries(
        fields.map((f) => [
          f.name,
          typeof stored[f.name] === 'string' ? (stored[f.name] as string) : '',
        ]),
      ),
    );
    setInited(true);
  }, [setting, isLoading, inited, fields]);

  if (isError) {
    return (
      <FormBanner tone="error">
        {title}: only administrators who manage integrations can see these settings.
      </FormBanner>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
      className="border-border/60 bg-surface rounded-2xl border shadow-sm"
    >
      <div className="border-border/60 border-b px-5 py-4">
        <h2 className="text-ink text-[15px] font-bold">{title}</h2>
        <p className="text-ink-muted mt-0.5 text-[13px] font-medium">{description}</p>
      </div>
      <div className="space-y-4 p-5">
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) =>
              f.options ? (
                <FormSelect
                  key={f.name}
                  label={f.label}
                  value={form[f.name] ?? ''}
                  onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                >
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </FormSelect>
              ) : (
                <Input
                  key={f.name}
                  label={f.label}
                  type={f.secret ? 'password' : 'text'}
                  autoComplete="off"
                  placeholder={f.placeholder}
                  hint={f.secret ? (f.hint ?? 'Stored encrypted. Leave as is to keep it.') : f.hint}
                  value={form[f.name] ?? ''}
                  onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                />
              ),
            )}
          </div>
        )}
        {mutation.isError && (
          <FormBanner tone="error">
            {(mutation.error as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message ?? 'Could not save.'}
          </FormBanner>
        )}
        {mutation.isSuccess && (
          <FormBanner tone="success">
            Saved. The live site uses it from the next request.
          </FormBanner>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {testName ? <IntegrationTestButton name={testName} /> : <span />}
          <button
            type="submit"
            disabled={mutation.isPending || isLoading}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center gap-2 rounded-xl px-6 text-[13px] font-bold text-white disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STAFFS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function StaffsPage() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [adminRoleId, setAdminRoleId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editAdminRoleId, setEditAdminRoleId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-staffs-list'],
    queryFn: () => api.users.listUsers({ role: 'ADMIN_STAFF', pageSize: 100 }),
  });

  const { data: rolesData } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.roles.listAdminRoles(),
  });

  const setStatusMutation = useMutation({
    mutationFn: (body: { id: string; status: string }) => api.users.setStatus(body.id, body.status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-staffs-list'] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.message || err?.message || 'Failed to update status');
    },
  });

  const createStaffMutation = useMutation({
    mutationFn: (body: any) => api.users.createUser(body),
    onSuccess: () => {
      setName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setShowAddForm(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-staffs-list'] });
    },
    onError: (err: any) => {
      // The API client might throw an AxiosError which contains a response.data.message
      setError(err?.response?.data?.message || err?.message || 'Failed to create staff');
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !phone || !password) return;
    createStaffMutation.mutate({
      name,
      email,
      phone,
      password,
      roles: ['ADMIN_STAFF'],
      ...(adminRoleId && { adminRoleId }),
    });
  };

  const updateStaffMutation = useMutation({
    mutationFn: (body: any) => api.users.updateUser(body.id, body),
    onSuccess: () => {
      setEditingStaffId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-staffs-list'] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.message || err?.message || 'Failed to update staff');
    },
  });

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStaffId) return;
    const data: any = {
      id: editingStaffId,
      name: editName,
      email: editEmail,
      phone: editPhone,
      adminRoleId: editAdminRoleId || null,
    };
    if (editPassword) data.password = editPassword;
    updateStaffMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const staffs = data?.data || [];

  return (
    <div className="space-y-6 pb-8">
      <div className="mb-2 flex justify-end">
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          {showAddForm ? 'Close Form' : '+ Add New Staff'}
        </button>
      </div>
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <h3 className="text-ink-muted mb-4 text-[12px] font-bold uppercase tracking-wider">
              Create New Staff Member
            </h3>
            {error && <FormBanner tone="error">{error}</FormBanner>}
            <form className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" onSubmit={handleCreate}>
              <Input
                label="Name"
                placeholder="e.g. John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <Input
                label="Email"
                type="email"
                placeholder="e.g. john@terra.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                label="Phone (IN)"
                placeholder="e.g. +919876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <Input
                label="Password"
                type="password"
                placeholder="Minimum 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="text-ink mb-1.5 block text-[13px] font-semibold">
                  Role Assignment
                </label>
                <select
                  className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 h-9 w-full rounded-xl border px-3 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
                  value={adminRoleId}
                  onChange={(e) => setAdminRoleId(e.target.value)}
                >
                  <option value="">Full Access (Super Admin)</option>
                  {rolesData?.map((r: any) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end sm:col-span-2 lg:col-span-4">
                <Button
                  type="submit"
                  disabled={createStaffMutation.isPending}
                  className="h-10 w-full sm:w-auto"
                >
                  {createStaffMutation.isPending ? 'Saving...' : 'Create Staff Member'}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="p-0">
          {staffs.length === 0 ? (
            <div className="py-12">
              <EmptyState
                icon={<Users className="text-ink-muted h-10 w-10" />}
                title="No staff members found"
                description="You have not added any additional staff members yet."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="py-3 pl-5 pr-3">#</th>
                    <th className="py-3 pr-4">Staff Details</th>
                    <th className="py-3 pr-4">Contact Info</th>
                    <th className="py-3 pr-4">Role</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4 text-center">Active</th>
                    <th className="py-3 pl-4 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  <AnimatePresence>
                    {staffs.map((staff, idx) => {
                      const isActive = staff.status === 'ACTIVE';
                      return (
                        <motion.tr
                          key={staff.id}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.02 }}
                          className="hover:bg-surface-subtle/30 group transition-colors"
                        >
                          <td className="text-ink-muted py-3 pl-5 pr-3 font-medium">{idx + 1}</td>
                          <td className="py-3 pr-4">
                            {editingStaffId === staff.id ? (
                              <div className="space-y-2">
                                <Input
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  className="w-full text-[13px]"
                                  placeholder="Name"
                                />
                                <Input
                                  value={editPassword}
                                  onChange={(e) => setEditPassword(e.target.value)}
                                  placeholder="New password (optional)"
                                  type="password"
                                  className="w-full text-[13px]"
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
                                  {staff.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-ink truncate font-semibold">{staff.name}</p>
                                  <p className="text-ink-subtle truncate text-xs">
                                    ID: {staff.id.slice(0, 8)}...
                                  </p>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="text-ink-muted py-3 pr-4">
                            {editingStaffId === staff.id ? (
                              <div className="space-y-2">
                                <Input
                                  value={editEmail}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                  className="w-full text-[13px]"
                                  placeholder="Email"
                                />
                                <Input
                                  value={editPhone}
                                  onChange={(e) => setEditPhone(e.target.value)}
                                  className="w-full text-[13px]"
                                  placeholder="Phone"
                                />
                              </div>
                            ) : (
                              <div className="space-y-0.5 text-[12px]">
                                <p className="flex items-center gap-1.5">
                                  <Mail className="text-ink-subtle h-3 w-3" />
                                  <span className="max-w-[140px] truncate">{staff.email}</span>
                                </p>
                                <p className="flex items-center gap-1.5">
                                  <Phone className="text-ink-subtle h-3 w-3" />
                                  {staff.phone || 'N/A'}
                                </p>
                              </div>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            {editingStaffId === staff.id ? (
                              <select
                                className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 h-9 w-full rounded-xl border px-3 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
                                value={editAdminRoleId}
                                onChange={(e) => setEditAdminRoleId(e.target.value)}
                              >
                                <option value="">Full Access</option>
                                {rolesData?.map((r: any) => (
                                  <option key={r.id} value={r.id}>
                                    {r.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <Badge tone={staff.adminRoleId ? 'neutral' : 'success'}>
                                {staff.adminRoleId
                                  ? rolesData?.find((r: any) => r.id === staff.adminRoleId)?.name ||
                                    'Custom Role'
                                  : 'Full Access'}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            {editingStaffId === staff.id ? (
                              <span className="text-ink-subtle text-xs italic">Editing...</span>
                            ) : (
                              <Badge
                                tone={
                                  staff.status === 'ACTIVE'
                                    ? 'success'
                                    : staff.status === 'DISABLED'
                                      ? 'danger'
                                      : 'neutral'
                                }
                              >
                                {staff.status}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setStatusMutation.mutate({
                                  id: staff.id,
                                  status: isActive ? 'DISABLED' : 'ACTIVE',
                                })
                              }
                              disabled={setStatusMutation.isPending}
                              aria-pressed={isActive}
                              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-300 disabled:opacity-50 ${isActive ? 'bg-gold-500' : 'bg-surface-subtle border-border/60 border'}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${isActive ? 'translate-x-4' : 'translate-x-0.5'}`}
                              />
                            </button>
                          </td>
                          <td className="py-3 pl-4 pr-5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {editingStaffId === staff.id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={handleUpdate}
                                    disabled={updateStaffMutation.isPending}
                                    className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                    title="Save"
                                  >
                                    {updateStaffMutation.isPending ? (
                                      <Loader2 className="h-[18px] w-[18px] animate-spin" />
                                    ) : (
                                      <Save className="h-[18px] w-[18px]" strokeWidth={2} />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingStaffId(null)}
                                    className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                    title="Cancel"
                                  >
                                    <X className="h-[18px] w-[18px]" strokeWidth={2} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                  title="Edit Staff"
                                  onClick={() => {
                                    setEditingStaffId(staff.id);
                                    setEditName(staff.name);
                                    setEditEmail(staff.email);
                                    setEditPhone(staff.phone || '');
                                    setEditAdminRoleId(staff.adminRoleId || '');
                                    setEditPassword('');
                                  }}
                                >
                                  <Settings2 className="h-[18px] w-[18px]" strokeWidth={2} />
                                </button>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. FEATURES ACTIVATION PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function FeaturesActivationPage() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'features_activation'],
    queryFn: () => api.cms.getSetting('features_activation').catch(() => null),
  });

  const mutation = useSettingMutation('features_activation');
  const values = (setting?.value as Record<string, boolean>) || {
    https: false,
    maintenance: false,
    disableImageEncoding: false,
    vendorSystem: true,
    classifiedProduct: true,
    walletSystem: false,
    couponSystem: false,
    pickupPoint: true,
    conversation: true,
    sellerProductManageAdmin: true,
    adminApprovalSellerProduct: true,
    emailVerification: true,
    productQuery: false,
    productExternalLinkSeller: false,
    floatingButtons: false,
  };

  const handleToggle = (key: string) => {
    const updated = { ...values, [key]: !values[key] };
    mutation.mutate(updated);
  };

  const FeatureToggleCard = ({
    title,
    field,
    description,
    badgeText,
  }: {
    title: string;
    field: string;
    description?: string;
    badgeText?: string;
  }) => {
    const active = values[field] ?? false;
    return (
      <div className="border-border/60 bg-surface flex h-full flex-col justify-between rounded-2xl border p-5 shadow-sm transition-shadow hover:shadow-md">
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-ink text-[14px] font-bold leading-snug">{title}</h3>
            <button
              type="button"
              onClick={() => handleToggle(field)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                active ? 'bg-ink' : 'bg-surface-subtle'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  active ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {description && (
            <p className="text-ink-muted text-[13px] font-medium leading-relaxed">{description}</p>
          )}
        </div>
        {badgeText && (
          <div className="mt-4">
            <span className="bg-surface-subtle text-ink-muted inline-flex items-center rounded-md px-2 py-1 text-[11px] font-bold">
              {badgeText}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8 pb-8">
      {/* Topbar renders the title, no local header needed here */}

      {isLoading ? (
        <div className="grid min-h-[300px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-10">
          <div>
            <h2 className="text-ink mb-5 text-[15px] font-extrabold uppercase tracking-wider">
              System
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FeatureToggleCard
                title="HTTPS Activation"
                field="https"
                description="Forces secure SSL protocol across the entire app."
              />
              <FeatureToggleCard
                title="Maintenance Mode Activation"
                field="maintenance"
                description="Places storefront in offline maintenance layout mode."
              />
              <FeatureToggleCard
                title="Disable Image Encoding"
                field="disableImageEncoding"
                description="Disables auto-conversion of uploaded assets."
              />
            </div>
          </div>

          <div>
            <h2 className="text-ink mb-5 text-[15px] font-extrabold uppercase tracking-wider">
              Business Related
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FeatureToggleCard
                title="Vendor System Activation"
                field="vendorSystem"
                description="Enables marketplace capability letting sellers register and list items."
              />
              <FeatureToggleCard
                title="Classified Product"
                field="classifiedProduct"
                description="Allows second-hand classified listings from customers."
              />
              <FeatureToggleCard
                title="Wallet System Activation"
                field="walletSystem"
                description="Enables credit, cashbacks, and transactions via customer wallets."
              />
              <FeatureToggleCard
                title="Coupon System Activation"
                field="couponSystem"
                description="Applies promotional codes and discounts at checkout."
              />
              <FeatureToggleCard
                title="Pickup Point Activation"
                field="pickupPoint"
                description="Supports self-pickup shipping methods for customers."
              />
              <FeatureToggleCard
                title="Conversation Activation"
                field="conversation"
                description="Enables support threads and vendor-customer live messaging."
              />
              <FeatureToggleCard
                title="Seller Product Manage By Admin"
                field="sellerProductManageAdmin"
                description="After activating this option, Cash On Delivery of Seller product will be managed by Admin."
                badgeText="Marketplace Rule"
              />
              <FeatureToggleCard
                title="Admin Approval On Seller Product"
                field="adminApprovalSellerProduct"
                description="After activating this option, Admin approval is needed for seller products."
                badgeText="Moderation Rule"
              />
              <FeatureToggleCard
                title="Email Verification"
                field="emailVerification"
                description="Requires email confirmation for new customer sign-ups. SMTP needs setting up."
                badgeText="Security"
              />
              <FeatureToggleCard
                title="Product Query Activation"
                field="productQuery"
                description="Allows public queries on catalog product details pages."
              />
              <FeatureToggleCard
                title="Product External Link for Seller"
                field="productExternalLinkSeller"
                description="Allows sellers to redirect checkouts to third-party domains."
              />
              <FeatureToggleCard
                title="Use Floating Buttons In Website"
                field="floatingButtons"
                description="Enables quick-contact floating dial buttons on the customer pages."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. LANGUAGES PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function LanguagesPage() {
  const queryClient = useQueryClient();
  const { data: locales, isLoading } = useQuery({
    queryKey: ['locales-list'],
    queryFn: () => api.globalization.listLocales(),
  });

  const { data: defaultLangSetting } = useQuery({
    queryKey: ['website-setting', 'default_language'],
    queryFn: () => api.cms.getSetting('default_language').catch(() => null),
  });

  const defaultMutation = useSettingMutation('default_language');
  const upsertLocaleMutation = useMutation({
    mutationFn: (body: {
      code: string;
      name: string;
      nativeName?: string;
      rtl?: boolean;
      active?: boolean;
    }) => api.globalization.upsertLocale(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['locales-list'] });
    },
  });

  const [selectedDefault, setSelectedDefault] = useState('');
  const [newLangName, setNewLangName] = useState('');
  const [newLangCode, setNewLangCode] = useState('');
  const [newLangFlutterCode, setNewLangFlutterCode] = useState('');
  const [newLangRtl, setNewLangRtl] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const saveDefaultLang = () => {
    if (selectedDefault) {
      defaultMutation.mutate({ code: selectedDefault });
    }
  };

  const handleAddLanguage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLangName || !newLangCode) return;
    upsertLocaleMutation.mutate(
      {
        code: newLangCode,
        name: newLangName,
        nativeName: newLangFlutterCode || undefined,
        rtl: newLangRtl,
        active: true,
      } as any,
      {
        onSuccess: () => {
          setNewLangName('');
          setNewLangCode('');
          setNewLangFlutterCode('');
          setNewLangRtl(false);
          setShowAddForm(false);
        },
      },
    );
  };

  const toggleRtl = (locale: any) => {
    upsertLocaleMutation.mutate({
      code: locale.code,
      name: locale.name,
      nativeName: locale.nativeName ?? undefined,
      rtl: !locale.rtl,
      active: locale.active,
    });
  };

  const toggleStatus = (locale: any) => {
    upsertLocaleMutation.mutate({
      code: locale.code,
      name: locale.name,
      nativeName: locale.nativeName ?? undefined,
      rtl: locale.rtl,
      active: !locale.active,
    });
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // 'en-IN', not 'en'. The seeded locales are en-IN / hi-IN / en-US / ar-AE, so a
  // fallback of 'en' matches no option and the browser then displays whichever
  // one happens to be first in the list — which is how this select came to show
  // "Arabic (UAE)" on a platform whose default is English (India). Saving is
  // guarded on an explicit selection, so nothing was ever written; it only
  // showed an administrator a language the storefront was not using.
  const defaultCode = (defaultLangSetting?.value as any)?.code || 'en-IN';

  return (
    <div className="space-y-8 pb-8">
      {/* Topbar renders the title, no local header needed here */}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Default Language Selector */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Default Language</h2>
          </div>
          <div className="space-y-4 p-5">
            <FormSelect
              label="Default Language"
              value={selectedDefault || defaultCode}
              onChange={(e) => setSelectedDefault(e.target.value)}
            >
              {locales?.map((l) => (
                <option key={l.id} value={l.code}>
                  {l.name} ({l.code})
                </option>
              ))}
            </FormSelect>
            <Button
              onClick={saveDefaultLang}
              disabled={defaultMutation.isPending}
              className="flex items-center gap-2"
            >
              {defaultMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Configuration
            </Button>
          </div>
        </div>

        {/* Translation file Import */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Import App Translations</h2>
          </div>
          <div className="space-y-4 p-5">
            <div>
              <label className="text-ink mb-1.5 block text-[13px] font-semibold">
                English Translation File (.arb)
              </label>
              <div className="flex items-center gap-3">
                <Button variant="outline" type="button" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Choose File
                </Button>
                <span className="text-ink-muted text-xs font-medium">No file chosen</span>
              </div>
            </div>
            <Button className="flex items-center gap-2">Import Translation</Button>
          </div>
        </div>
      </div>

      {/* Languages Table */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-ink text-[15px] font-bold">Supported Languages</h2>
          <Button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add New Language
          </Button>
        </div>

        <div className="p-0">
          {showAddForm && (
            <div className="border-border/60 bg-surface-subtle border-b p-5">
              <form onSubmit={handleAddLanguage} className="space-y-4">
                <h3 className="text-ink text-[13px] font-bold uppercase tracking-wider">
                  Add New Locale
                </h3>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Input
                    label="Language Name"
                    placeholder="e.g. French"
                    value={newLangName}
                    onChange={(e) => setNewLangName(e.target.value)}
                    required
                  />
                  <Input
                    label="Code"
                    placeholder="e.g. fr"
                    value={newLangCode}
                    onChange={(e) => setNewLangCode(e.target.value)}
                    required
                  />
                  <Input
                    label="Flutter App Lang Code"
                    placeholder="e.g. fr"
                    value={newLangFlutterCode}
                    onChange={(e) => setNewLangFlutterCode(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="newLangRtl"
                    checked={newLangRtl}
                    onChange={(e) => setNewLangRtl(e.target.checked)}
                    className="text-ink border-border/60 rounded"
                  />
                  <label htmlFor="newLangRtl" className="text-ink text-[13px] font-medium">
                    Right-to-Left (RTL) Layout?
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={upsertLocaleMutation.isPending}>
                    {upsertLocaleMutation.isPending ? 'Saving...' : 'Add Language'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-subtle text-ink-muted">
                <tr>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">#</th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    App Code
                  </th>
                  <th className="px-5 py-3.5 text-center text-[11px] font-bold uppercase tracking-wider">
                    RTL
                  </th>
                  <th className="px-5 py-3.5 text-center text-[11px] font-bold uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/60 divide-y">
                {locales?.map((locale, idx) => (
                  <tr key={locale.id} className="hover:bg-surface-subtle transition-colors">
                    <td className="text-ink-muted px-5 py-4 font-medium">{idx + 1}</td>
                    <td className="text-ink px-5 py-4 font-bold">{locale.name}</td>
                    <td className="text-ink-muted px-5 py-4 font-medium">{locale.code}</td>
                    <td className="text-ink-muted px-5 py-4 font-medium">
                      {locale.nativeName || 'N/A'}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => toggleRtl(locale)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none ${
                          locale.rtl ? 'bg-ink' : 'bg-surface-subtle'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ${
                            locale.rtl ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => toggleStatus(locale)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none ${
                          locale.active ? 'bg-emerald-500' : 'bg-surface-subtle'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ${
                            locale.active ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. CURRENCY PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function CurrencyPage() {
  const queryClient = useQueryClient();
  const { data: currencies, isLoading } = useQuery({
    queryKey: ['currencies-list'],
    queryFn: () => api.globalization.listCurrencies(),
  });

  const { data: formatSetting } = useQuery({
    queryKey: ['website-setting', 'currency_formats'],
    queryFn: () => api.cms.getSetting('currency_formats').catch(() => null),
  });

  const { data: defaultCurrencySetting } = useQuery({
    queryKey: ['website-setting', 'default_currency'],
    queryFn: () => api.cms.getSetting('default_currency').catch(() => null),
  });

  const formatMutation = useSettingMutation('currency_formats');
  const defaultCurrencyMutation = useSettingMutation('default_currency');

  const upsertCurrencyMutation = useMutation({
    mutationFn: (body: {
      code: string;
      name: string;
      symbol: string;
      minorUnits?: number;
      rateToInrX10000?: number;
      active?: boolean;
    }) => api.globalization.upsertCurrency(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['currencies-list'] });
    },
  });

  const [selectedDefaultCurrency, setSelectedDefaultCurrency] = useState('');
  const [symbolFormat, setSymbolFormat] = useState('[Symbol][Amount]');
  const [decimalSep, setDecimalSep] = useState('1,23,456.70');
  const [noOfDecimals, setNoOfDecimals] = useState('2');
  const [showAddForm, setShowAddForm] = useState(false);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [exchangeRate, setExchangeRate] = useState('1.0');

  const saveFormats = () => {
    formatMutation.mutate({ symbolFormat, decimalSep, noOfDecimals });
  };

  const handleAddCurrency = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || !name || !symbol) return;
    upsertCurrencyMutation.mutate(
      {
        code: code.toUpperCase(),
        name,
        symbol,
        minorUnits: parseInt(noOfDecimals) || 2,
        rateToInrX10000: Math.round(parseFloat(exchangeRate) * 10000),
        active: true,
      },
      {
        onSuccess: () => {
          setCode('');
          setName('');
          setSymbol('');
          setExchangeRate('1.0');
          setShowAddForm(false);
        },
      },
    );
  };

  const toggleCurrencyStatus = (curr: any) => {
    upsertCurrencyMutation.mutate({
      code: curr.code,
      name: curr.name,
      symbol: curr.symbol,
      minorUnits: curr.minorUnits,
      rateToInrX10000: curr.rateToInrX10000,
      active: !curr.active,
    });
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const defaultCurrencyCode = (defaultCurrencySetting?.value as any)?.code || 'INR';

  const savedFormats = (formatSetting?.value as any) || {
    symbolFormat: '[Symbol][Amount]',
    decimalSep: '1,23,456.70',
    noOfDecimals: '2',
  };

  return (
    <div className="space-y-8 pb-8">
      {/* Topbar renders the title, no local header needed here */}

      <div className="grid gap-6 md:grid-cols-2">
        {/* System Default Currency */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">System Default Currency</h2>
          </div>
          <div className="space-y-4 p-5">
            <FormSelect
              label="System Default Currency"
              value={selectedDefaultCurrency || defaultCurrencyCode}
              onChange={(e) => setSelectedDefaultCurrency(e.target.value)}
            >
              {currencies?.map((c) => (
                <option key={c.id} value={c.code}>
                  {c.name} ({c.symbol})
                </option>
              ))}
            </FormSelect>
            <Button
              onClick={() => {
                const code = selectedDefaultCurrency || defaultCurrencyCode;
                if (code) {
                  defaultCurrencyMutation.mutate({ code });
                }
              }}
              disabled={defaultCurrencyMutation.isPending}
              className="flex items-center gap-2"
            >
              {defaultCurrencyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Configuration
            </Button>
          </div>
        </div>

        {/* Currency Formats */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Set Currency Formats</h2>
          </div>
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormSelect
                label="Symbol Format"
                value={symbolFormat || savedFormats.symbolFormat}
                onChange={(e) => setSymbolFormat(e.target.value)}
              >
                <option value="[Symbol][Amount]">[Symbol][Amount]</option>
                <option value="[Amount][Symbol]">[Amount][Symbol]</option>
                <option value="[Symbol] [Amount]">[Symbol] [Amount]</option>
              </FormSelect>
              <FormSelect
                label="Decimal Separator"
                value={decimalSep || savedFormats.decimalSep}
                onChange={(e) => setDecimalSep(e.target.value)}
              >
                <option value="1,23,456.70">1,23,456.70</option>
                <option value="123,456.70">123,456.70</option>
                <option value="123.456,70">123.456,70</option>
              </FormSelect>
              <div className="sm:col-span-2">
                <FormSelect
                  label="No of decimals"
                  value={noOfDecimals || savedFormats.noOfDecimals}
                  onChange={(e) => setNoOfDecimals(e.target.value)}
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </FormSelect>
              </div>
            </div>
            <Button onClick={saveFormats} disabled={formatMutation.isPending}>
              {formatMutation.isPending ? 'Saving...' : 'Save Formats'}
            </Button>
          </div>
        </div>
      </div>

      {/* Currencies Table */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-ink text-[15px] font-bold">All Currencies</h2>
          <Button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add New Currency
          </Button>
        </div>
        <div className="p-0">
          {showAddForm && (
            <div className="border-border/60 bg-surface-subtle border-b p-5">
              <form onSubmit={handleAddCurrency} className="space-y-4">
                <h3 className="text-ink text-[13px] font-bold uppercase tracking-wider">
                  Add Custom Currency
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Input
                    label="Currency Name"
                    placeholder="e.g. Euro"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <Input
                    label="Code (3-letter)"
                    placeholder="e.g. EUR"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                  <Input
                    label="Symbol"
                    placeholder="e.g. €"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    required
                  />
                  <Input
                    label="Exchange Rate (1 USD = ?)"
                    placeholder="e.g. 0.92"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={upsertCurrencyMutation.isPending}>
                    {upsertCurrencyMutation.isPending ? 'Saving...' : 'Add Currency'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-subtle text-ink-muted">
                <tr>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">#</th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Currency Name
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Symbol
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Rate (1 USD = ?)
                  </th>
                  <th className="px-5 py-3.5 text-center text-[11px] font-bold uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/60 divide-y">
                {currencies?.map((curr, idx) => (
                  <tr key={curr.id} className="hover:bg-surface-subtle transition-colors">
                    <td className="text-ink-muted px-5 py-4 font-medium">{idx + 1}</td>
                    <td className="text-ink px-5 py-4 font-bold">{curr.name}</td>
                    <td className="text-ink px-5 py-4 font-bold">{curr.symbol}</td>
                    <td className="text-ink-muted px-5 py-4 font-medium">{curr.code}</td>
                    <td className="text-ink px-5 py-4 font-mono font-medium">
                      {curr.rateToInrX10000 / 10000}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => toggleCurrencyStatus(curr)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none ${
                          curr.active ? 'bg-emerald-500' : 'bg-surface-subtle'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ${
                            curr.active ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. VAT & TAX PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function VatTaxPage() {
  const queryClient = useQueryClient();
  const { data: taxRules, isLoading } = useQuery({
    queryKey: ['tax-rules-list'],
    queryFn: () => api.globalization.listTaxRules(),
  });

  const upsertTaxRuleMutation = useMutation({
    mutationFn: (body: {
      countryCode: string;
      kind: 'DEFAULT' | 'CATEGORY' | 'PRODUCT';
      rateBps: number;
      label?: string;
      active?: boolean;
    }) => api.globalization.upsertTaxRule(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tax-rules-list'] });
    },
  });

  const [countryCode, setCountryCode] = useState('IN');
  const [ratePercent, setRatePercent] = useState('18');
  const [label, setLabel] = useState('GST');
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAddTax = (e: React.FormEvent) => {
    e.preventDefault();
    if (!countryCode || !ratePercent) return;
    upsertTaxRuleMutation.mutate(
      {
        countryCode: countryCode.toUpperCase(),
        kind: 'DEFAULT',
        rateBps: Math.round(parseFloat(ratePercent) * 100),
        label,
        active: true,
      },
      {
        onSuccess: () => {
          setCountryCode('IN');
          setRatePercent('18');
          setLabel('GST');
          setShowAddForm(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-ink text-[14px] font-bold">All Taxes</h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
          >
            <Plus className="h-4 w-4" /> Add New Tax
          </button>
        </div>
        <div className="p-0">
          {showAddForm && (
            <div className="border-border/60 bg-surface-subtle border-b p-5">
              <form onSubmit={handleAddTax} className="space-y-4">
                <h3 className="text-ink text-[13px] font-bold uppercase tracking-wider">
                  Add New Tax Rule
                </h3>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Input
                    label="Country Code"
                    placeholder="e.g. IN"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    required
                  />
                  <Input
                    label="Tax rate (%)"
                    placeholder="e.g. 18"
                    value={ratePercent}
                    onChange={(e) => setRatePercent(e.target.value)}
                    required
                  />
                  <Input
                    label="Label Description"
                    placeholder="e.g. CGST+SGST"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={upsertTaxRuleMutation.isPending}>
                    {upsertTaxRuleMutation.isPending ? 'Saving...' : 'Add Tax'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-subtle text-ink-muted">
                <tr>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">#</th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Tax Type / Label
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Rate (%)
                  </th>
                  <th className="px-5 py-3.5 text-center text-[11px] font-bold uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/60 divide-y">
                {taxRules?.map((rule, idx) => (
                  <tr key={rule.id} className="hover:bg-surface-subtle transition-colors">
                    <td className="text-ink-muted px-5 py-4 font-medium">{idx + 1}</td>
                    <td className="text-ink px-5 py-4 font-bold">{rule.label || 'Default VAT'}</td>
                    <td className="text-ink-muted px-5 py-4 font-bold">{rule.countryCode}</td>
                    <td className="text-ink px-5 py-4 font-mono font-medium">
                      {rule.rateBps / 100}%
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-bold ring-1 ring-inset ${rule.active ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-gray-50 text-gray-700 ring-gray-600/20'}`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${rule.active ? 'bg-emerald-500' : 'bg-gray-500'}`}
                        />
                        {rule.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
                {(!taxRules || taxRules.length === 0) && (
                  <tr>
                    <td colSpan={5} className="text-ink-muted py-8 text-center text-sm font-medium">
                      No tax profiles configured. Fallback to default country tax rates.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. SMTP SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function SmtpSettingsPage() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'smtp_settings'],
    queryFn: () => api.cms.getSetting('smtp_settings').catch(() => null),
  });

  const mutation = useSettingMutation('smtp_settings');
  const values = useMemo(
    () =>
      (setting?.value as any) || {
        host: 'smtp.mailtrap.io',
        port: '2525',
        username: '',
        password: '',
        senderEmail: '',
        senderName: 'Terra Shoppy',
        encryption: 'tls',
      },
    [setting?.value],
  );

  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [encryption, setEncryption] = useState('tls');
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (setting && !inited) {
      setHost(values.host ?? '');
      setPort(values.port ?? '');
      setUsername(values.username ?? '');
      setPassword(values.password ?? '');
      setSenderEmail(values.senderEmail ?? '');
      setSenderName(values.senderName ?? '');
      setEncryption(values.encryption ?? 'tls');
      setInited(true);
    }
  }, [setting, inited, values]);

  const [testEmail, setTestEmail] = useState('');

  const testSmtpMutation = useMutation({
    mutationFn: (body: any) => api.cms.testSmtpSettings(body),
    onSuccess: () => {
      alert('Verification email sent successfully! Please check your inbox.');
    },
    onError: (err: any) => {
      alert(err?.response?.data?.message || err?.message || 'Failed to send verification email.');
    },
  });

  const handleTestSmtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail) return;
    testSmtpMutation.mutate({
      host: host || values.host,
      port: port || values.port,
      username: username || values.username,
      password: password || values.password,
      encryption: encryption || values.encryption,
      senderEmail: senderEmail || values.senderEmail,
      senderName: senderName || values.senderName,
      to: testEmail,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      host: host || values.host,
      port: port || values.port,
      username: username || values.username,
      password: password || values.password,
      senderEmail: senderEmail || values.senderEmail,
      senderName: senderName || values.senderName,
      encryption: encryption || values.encryption,
    });
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="grid gap-6 md:grid-cols-3">
        <form onSubmit={handleSubmit} className="space-y-6 md:col-span-2">
          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 border-b px-5 py-4">
              <h2 className="text-ink text-[15px] font-bold">Connection Configurations</h2>
            </div>
            <div className="space-y-5 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="SMTP Host"
                  placeholder={values.host}
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                />
                <Input
                  label="SMTP Port"
                  placeholder={values.port}
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                />
                <Input
                  label="Sender Name"
                  placeholder={values.senderName}
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                />
                <Input
                  label="Sender Email Address"
                  placeholder={values.senderEmail || 'noreply@terrashoppy.com'}
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                />
                <Input
                  label="SMTP Username"
                  placeholder={values.username || 'API UserKey'}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <Input
                  label="SMTP Password"
                  type="password"
                  placeholder={values.password ? '••••••••' : 'Password Key'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="sm:col-span-2">
                  <FormSelect
                    label="Mail Encryption Type"
                    value={encryption}
                    onChange={(e) => setEncryption(e.target.value)}
                  >
                    <option value="tls">STARTTLS (Standard Secure)</option>
                    <option value="ssl">SSL/TLS (Implicit Secure)</option>
                    <option value="none">None (Plaintext Port)</option>
                  </FormSelect>
                </div>
              </div>
            </div>
            <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
              >
                {mutation.isPending ? (
                  <Spinner size="sm" className="text-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Connection Details
              </button>
            </div>
          </div>
        </form>

        <div className="space-y-6">
          <form
            onSubmit={handleTestSmtp}
            className="border-border/60 bg-surface rounded-2xl border shadow-sm"
          >
            <div className="border-border/60 border-b px-5 py-4">
              <h2 className="text-ink text-[15px] font-bold">SMTP Verification</h2>
            </div>
            <div className="space-y-5 p-5">
              <p className="text-ink-muted text-[13px] font-medium">
                Send a quick test message to make sure your email credentials are safe and
                configured correctly.
              </p>
              <Input
                label="Test Destination Email"
                placeholder="you@domain.com"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                disabled={testSmtpMutation.isPending || !testEmail}
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle flex h-9 w-full items-center justify-center gap-2 rounded-xl border px-4 text-[13px] font-bold transition-colors disabled:opacity-50"
              >
                {testSmtpMutation.isPending ? <Spinner size="sm" /> : <Mail className="h-4 w-4" />}
                {testSmtpMutation.isPending ? 'Sending...' : 'Send Test Mail'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 7. PAYMENT METHODS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function PaymentMethodsPage() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'payment_methods'],
    queryFn: () => api.cms.getSetting('payment_methods').catch(() => null),
  });

  const mutation = useSettingMutation('payment_methods');
  const values = useMemo(
    () =>
      (setting?.value as any) || {
        codActive: true,
        stripeActive: false,
        stripePublicKey: '',
        stripeSecretKey: '',
        razorpayActive: false,
        razorpayKeyId: '',
        razorpayKeySecret: '',
        paypalActive: false,
        paypalClientId: '',
      },
    [setting?.value],
  );

  const [codActive, setCodActive] = useState(true);
  const [stripeActive, setStripeActive] = useState(false);
  const [stripePublicKey, setStripePublicKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [razorpayActive, setRazorpayActive] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState('');
  const [razorpayKeySecret, setRazorpayKeySecret] = useState('');
  const [razorpayWebhookSecret, setRazorpayWebhookSecret] = useState('');
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (setting && !inited) {
      setRazorpayWebhookSecret(values.razorpayWebhookSecret ?? '');
      setCodActive(values.codActive ?? true);
      setStripeActive(values.stripeActive ?? false);
      setStripePublicKey(values.stripePublicKey ?? '');
      setStripeSecretKey(values.stripeSecretKey ?? '');
      setRazorpayActive(values.razorpayActive ?? false);
      setRazorpayKeyId(values.razorpayKeyId ?? '');
      setRazorpayKeySecret(values.razorpayKeySecret ?? '');
      setInited(true);
    }
  }, [setting, inited, values]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      codActive,
      stripeActive,
      stripePublicKey,
      stripeSecretKey,
      razorpayActive,
      razorpayKeyId,
      razorpayKeySecret,
      razorpayWebhookSecret,
      paypalActive: values.paypalActive,
      paypalClientId: values.paypalClientId,
    });
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <form onSubmit={handleSave} className="space-y-6">
        {/* Cash On Delivery */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <h2 className="text-ink text-[15px] font-bold">Cash On Delivery (COD)</h2>
              <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
                Let clients pay cash upon delivery.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCodActive(!codActive)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none ${
                codActive ? 'bg-emerald-500' : 'bg-surface-subtle border-border/60 border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ${
                  codActive ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Stripe Gateway */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="text-ink text-[15px] font-bold">Stripe Credentials</h2>
              <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
                Configure Stripe card payments gateway.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setStripeActive(!stripeActive)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none ${
                stripeActive ? 'bg-emerald-500' : 'bg-surface-subtle border-border/60 border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ${
                  stripeActive ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Stripe Public Key"
                placeholder={values.stripePublicKey || 'pk_live_...'}
                value={stripePublicKey}
                onChange={(e) => setStripePublicKey(e.target.value)}
                disabled={!stripeActive}
              />
              <Input
                label="Stripe Secret Key"
                type="password"
                placeholder={values.stripeSecretKey ? '••••••••' : 'sk_live_...'}
                value={stripeSecretKey}
                onChange={(e) => setStripeSecretKey(e.target.value)}
                disabled={!stripeActive}
              />
            </div>
          </div>
        </div>

        {/* Razorpay Gateway */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="text-ink text-[15px] font-bold">Razorpay Credentials (India)</h2>
              <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
                Razorpay native card, UPI, and net banking split checkouts.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRazorpayActive(!razorpayActive)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none ${
                razorpayActive ? 'bg-emerald-500' : 'bg-surface-subtle border-border/60 border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ${
                  razorpayActive ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Razorpay Key ID"
                placeholder={values.razorpayKeyId || 'rzp_live_...'}
                value={razorpayKeyId}
                onChange={(e) => setRazorpayKeyId(e.target.value)}
                disabled={!razorpayActive}
              />
              <Input
                label="Razorpay Secret Key"
                type="password"
                placeholder={values.razorpayKeySecret ? '••••••••' : 'secret...'}
                value={razorpayKeySecret}
                onChange={(e) => setRazorpayKeySecret(e.target.value)}
                disabled={!razorpayActive}
              />
              <Input
                label="Razorpay Webhook Secret"
                type="password"
                autoComplete="off"
                placeholder="the secret set on the Razorpay webhook"
                hint="Stored encrypted. Must match Razorpay → Webhooks, or payments stay unconfirmed."
                value={razorpayWebhookSecret}
                onChange={(e) => setRazorpayWebhookSecret(e.target.value)}
                disabled={!razorpayActive}
              />
            </div>
            <IntegrationTestButton name="razorpay" />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center gap-2 rounded-xl px-6 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Payment Integrations
          </button>
        </div>
      </form>

      <SettingCard
        settingKey="payout_settings"
        title="Seller payouts (RazorpayX)"
        description="Sends seller payouts from your RazorpayX current account, using the Razorpay keys above."
        testName="razorpayx"
        fields={[
          {
            name: 'razorpayxAccountNumber',
            label: 'RazorpayX account number',
            placeholder: 'e.g. 7878780080316316',
            hint: 'RazorpayX → Account details. Blank uses the server environment.',
          },
          {
            name: 'razorpayxWebhookSecret',
            label: 'RazorpayX webhook secret',
            secret: true,
            placeholder: 'the secret on the payouts webhook',
          },
        ]}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 8. ORDER CONFIGURATION PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function OrderConfigurationPage() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'order_config'],
    queryFn: () => api.cms.getSetting('order_config').catch(() => null),
  });

  const mutation = useSettingMutation('order_config');
  const values = useMemo(
    () =>
      (setting?.value as any) || {
        minimumOrderAmount: '0',
        allowGuestCheckout: true,
        requireBillingAddress: true,
        invoicePrefix: 'TS',
      },
    [setting?.value],
  );

  const [minimumOrderAmount, setMinimumOrderAmount] = useState('');
  const [allowGuestCheckout, setAllowGuestCheckout] = useState(true);
  const [requireBillingAddress, setRequireBillingAddress] = useState(true);
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (setting && !inited) {
      setMinimumOrderAmount(values.minimumOrderAmount ?? '0');
      setAllowGuestCheckout(values.allowGuestCheckout ?? true);
      setRequireBillingAddress(values.requireBillingAddress ?? true);
      setInvoicePrefix(values.invoicePrefix ?? 'TS');
      setInited(true);
    }
  }, [setting, inited, values]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      minimumOrderAmount: minimumOrderAmount || values.minimumOrderAmount,
      allowGuestCheckout,
      requireBillingAddress,
      invoicePrefix: invoicePrefix || values.invoicePrefix,
    });
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <form onSubmit={handleSave} className="space-y-6">
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Fulfillment Settings</h2>
            <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
              Manage storefront checkout flow properties, thresholds, and invoice options.
            </p>
          </div>
          <div className="space-y-5 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Minimum Order amount (paise)"
                placeholder={values.minimumOrderAmount}
                value={minimumOrderAmount}
                onChange={(e) => setMinimumOrderAmount(e.target.value)}
              />
              <Input
                label="Invoice Sequential Prefix"
                placeholder={values.invoicePrefix}
                value={invoicePrefix}
                onChange={(e) => setInvoicePrefix(e.target.value)}
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="allowGuest"
                  checked={allowGuestCheckout}
                  onChange={(e) => setAllowGuestCheckout(e.target.checked)}
                  className="accent-ink h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="allowGuest" className="text-ink text-[14px] font-bold">
                  Allow Guest Checkout (No accounts required)
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="requireBilling"
                  checked={requireBillingAddress}
                  onChange={(e) => setRequireBillingAddress(e.target.checked)}
                  className="accent-ink h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="requireBilling" className="text-ink text-[14px] font-bold">
                  Require distinct Billing Addresses
                </label>
              </div>
            </div>
          </div>
          <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
            >
              {mutation.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Order Configurations
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 8b. WALLET REDEMPTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * How much wallet balance a customer may spend on one order.
 *
 * The percentage applies to **reward** money only — cashback, referral earnings
 * and promotional credit — which is what makes a balance a discount spread over
 * many orders rather than one free one.
 *
 * It deliberately does not apply to a customer's own money: a top-up they paid
 * for by card, or a refund of an order they already paid for. Capping those would
 * mean holding on to money that is theirs, so the server never does, whatever
 * this is set to. The copy below says so, because an operator setting "10%" will
 * otherwise reasonably assume it covers the whole balance.
 */
export function WalletRedemptionPage() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'wallet_redemption'],
    queryFn: () => api.cms.getSetting('wallet_redemption').catch(() => null),
  });

  const mutation = useSettingMutation('wallet_redemption');
  const values = useMemo(
    () =>
      (setting?.value as any) || {
        enabled: true,
        maxPercentOfOrder: 10,
        minOrderPaise: 0,
      },
    [setting?.value],
  );

  const [enabled, setEnabled] = useState(true);
  const [maxPercent, setMaxPercent] = useState('10');
  const [minOrderRupees, setMinOrderRupees] = useState('0');
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (setting && !inited) {
      setEnabled(values.enabled ?? true);
      setMaxPercent(String(values.maxPercentOfOrder ?? 10));
      // Shown in rupees, stored in paise — an operator should not have to
      // multiply by a hundred to set a ₹500 floor.
      setMinOrderRupees(String(Math.round((values.minOrderPaise ?? 0) / 100)));
      setInited(true);
    }
  }, [setting, inited, values]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const percent = Number(maxPercent);
    const rupees = Number(minOrderRupees);
    mutation.mutate({
      enabled,
      maxPercentOfOrder: Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 10,
      minOrderPaise: Number.isFinite(rupees) ? Math.max(0, Math.round(rupees * 100)) : 0,
    });
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <form onSubmit={handleSave} className="space-y-6">
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Wallet at Checkout</h2>
            <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
              How much reward balance — cashback, referral earnings, promo credit — a customer may
              put towards a single order.
            </p>
          </div>
          <div className="space-y-5 p-5">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="redemptionEnabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="accent-ink h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="redemptionEnabled" className="text-ink text-[14px] font-bold">
                Let customers spend reward balance at checkout
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Maximum share of an order (%)"
                type="number"
                min={0}
                max={100}
                value={maxPercent}
                onChange={(e) => setMaxPercent(e.target.value)}
              />
              <Input
                label="Minimum order value to redeem on (₹)"
                type="number"
                min={0}
                value={minOrderRupees}
                onChange={(e) => setMinOrderRupees(e.target.value)}
              />
            </div>

            <p className="text-ink-muted bg-surface-subtle/40 rounded-xl p-3 text-[13px] font-medium">
              This limit covers reward money only. Money a customer topped up themselves, or a
              refund of an order they already paid for, is theirs and stays fully spendable whatever
              this is set to. Wallet balance cannot be combined with cash on delivery, because the
              courier is told a single amount to collect.
            </p>
          </div>
          <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
            >
              {mutation.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Wallet Settings
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 13. SHIPPING PAGE
// ═══════════════════════════════════════════════════════════════════════════════

interface ShippingMethod {
  id: string;
  zone: string;
  methodName: string;
  costPaise: number;
  freeLimitPaise: number;
}

export function ShippingPage() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'shipping_settings'],
    queryFn: () => api.cms.getSetting('shipping_settings').catch(() => null),
  });

  const mutation = useSettingMutation('shipping_settings');
  const methods = (setting?.value as ShippingMethod[]) || [
    {
      id: '1',
      zone: 'Domestic (India)',
      methodName: 'Flat Rate Standard',
      costPaise: 4900,
      freeLimitPaise: 99900,
    },
    {
      id: '2',
      zone: 'International',
      methodName: 'Premium Express DHL',
      costPaise: 199000,
      freeLimitPaise: 999900,
    },
  ];

  const [zone, setZone] = useState('');
  const [methodName, setMethodName] = useState('');
  const [cost, setCost] = useState('0');
  const [freeLimit, setFreeLimit] = useState('0');
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!zone || !methodName) return;
    const newMethod: ShippingMethod = {
      id: crypto.randomUUID(),
      zone,
      methodName,
      costPaise: Math.round(parseFloat(cost) * 100) || 0,
      freeLimitPaise: Math.round(parseFloat(freeLimit) * 100) || 0,
    };
    mutation.mutate([...methods, newMethod]);
    setZone('');
    setMethodName('');
    setCost('0');
    setFreeLimit('0');
    setShowAddForm(false);
  };

  const handleDelete = (id: string) => {
    const updated = methods.filter((m) => m.id !== id);
    mutation.mutate(updated);
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-ink text-[14px] font-bold">Shipping Zones & Rates</h2>
            <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
              Manage shipping channels, flat rates, international delivery zones, and free limits.
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
          >
            <Plus className="h-4 w-4" /> Add Shipping Method
          </button>
        </div>
        <div className="p-0">
          {showAddForm && (
            <div className="border-border/60 bg-surface-subtle border-b p-5">
              <form onSubmit={handleAdd} className="space-y-4">
                <h3 className="text-ink text-[13px] font-bold uppercase tracking-wider">
                  New Shipping Delivery Slab
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Shipping Zone / Country"
                    placeholder="e.g. Domestic (India)"
                    value={zone}
                    onChange={(e) => setZone(e.target.value)}
                    required
                  />
                  <Input
                    label="Method Description"
                    placeholder="e.g. Standard Surface"
                    value={methodName}
                    onChange={(e) => setMethodName(e.target.value)}
                    required
                  />
                  <Input
                    label="Fulfillment Delivery Cost (Paise)"
                    placeholder="4900"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                  />
                  <Input
                    label="Free Shipping Minimum Limit (Paise)"
                    placeholder="99900"
                    value={freeLimit}
                    onChange={(e) => setFreeLimit(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Add Shipping Method</Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-subtle text-ink-muted">
                <tr>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Zone
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Method Name
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Cost (paise)
                  </th>
                  <th className="px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider">
                    Free Shipping Minimum Limit
                  </th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-bold uppercase tracking-wider">
                    Options
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/60 divide-y">
                {methods.map((curr) => (
                  <tr key={curr.id} className="hover:bg-surface-subtle transition-colors">
                    <td className="text-ink px-5 py-4 font-bold">{curr.zone}</td>
                    <td className="text-ink-muted px-5 py-4 font-medium">{curr.methodName}</td>
                    <td className="text-ink px-5 py-4 font-mono font-medium">
                      ₹{curr.costPaise / 100}
                    </td>
                    <td className="px-5 py-4 font-mono font-bold text-emerald-600">
                      {curr.freeLimitPaise > 0
                        ? `Free above ₹${curr.freeLimitPaise / 100}`
                        : 'No free limits'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(curr.id)}
                        className="text-ink-muted inline-grid h-8 w-8 place-items-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * What each integration is actually using right now.
 *
 * The forms below render the `website_settings` row and nothing else, so on a
 * deployment configured through the server environment they show empty fields
 * and read as "nothing is set up" — while OTPs, invoices and shipments go out
 * normally. Worse, the forms write back what they display, so saving a page
 * that was lying stored the lie.
 *
 * This panel is the other half of that picture: it asks the server what is in
 * effect. Blank fields below mean "inherit from the server", not "unconfigured".
 */
function EffectiveConfigPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings-effective'],
    queryFn: () => api.cms.getEffectiveConfig().catch(() => null),
    staleTime: 30_000,
  });

  if (isLoading || !data) return null;

  const sourceLabel: Record<string, string> = {
    db: 'set on this page',
    env: 'from the server environment',
    mixed: 'part here, part from the server',
    none: 'not configured anywhere',
  };

  const rows: Array<{
    label: string;
    ok: boolean;
    detail: string;
    source: string;
    warn?: string | undefined;
  }> = [
    {
      label: 'SMS gateway',
      ok: data.sms.configured && data.sms.provider !== 'console',
      detail: data.sms.configured
        ? `${
            data.sms.provider === 'bizbrick'
              ? 'BizBrick'
              : data.sms.provider === 'smsindiahub'
                ? 'SMS India Hub'
                : 'Console logger'
          }${data.sms.senderId ? ` · sender ${data.sms.senderId}` : ''}`
        : 'No gateway credentials',
      source: sourceLabel[data.sms.source] ?? data.sms.source,
      warn: data.sms.overruled
        ? 'A stored "Console Logger" selection is being ignored — honouring it would stop every sign-in code.'
        : !data.sms.endpointIsHttps && data.sms.configured
          ? 'The gateway endpoint is plain HTTP; the API key travels unencrypted.'
          : undefined,
    },
    {
      label: 'Email (SMTP)',
      ok: data.email.configured,
      detail: data.email.configured
        ? `${data.email.host ?? ''}${data.email.senderEmail ? ` · ${data.email.senderEmail}` : ''}`
        : 'Falls back to the console adapter',
      source: sourceLabel[data.email.source] ?? data.email.source,
      warn: data.email.quotedValue
        ? 'The configured value carries literal quotation marks — usually a typo in an env file.'
        : undefined,
    },
    {
      label: 'Payments (Razorpay)',
      ok: data.payments.configured && data.payments.key?.mode === 'live',
      detail: data.payments.key
        ? `${data.payments.key.mode === 'live' ? 'LIVE' : data.payments.key.mode.toUpperCase()} key · ${data.payments.key.hint}`
        : 'No key configured',
      source: sourceLabel[data.payments.source] ?? data.payments.source,
      warn: !data.payments.webhookSecretSet
        ? 'No webhook secret: the gateway callback is refused, so paid orders never leave Pending Payment.'
        : data.payments.key?.mode === 'test'
          ? 'A test key is live — real payments will not be captured.'
          : undefined,
    },
    {
      label: 'Shipping (Shiprocket)',
      ok: data.shipping.configured && data.shipping.webhookTokenSet,
      detail: data.shipping.configured
        ? (data.shipping.account ?? 'Account configured')
        : 'Using the placeholder account',
      source: sourceLabel[data.shipping.source] ?? data.shipping.source,
      warn: !data.shipping.webhookTokenSet
        ? 'No webhook token: courier status callbacks are refused, so tracking never advances past dispatch.'
        : data.shipping.quotedValue
          ? 'The credentials carry literal quotation marks — sign-in to the courier will fail.'
          : undefined,
    },
    {
      label: 'Media storage (Cloudinary)',
      ok: data.storage.configured,
      detail: data.storage.configured
        ? (data.storage.cloudName ?? 'Configured')
        : 'Uploads will fail',
      source: sourceLabel[data.storage.source] ?? data.storage.source,
    },
    {
      label: 'Maps (MapmyIndia)',
      ok: data.maps.configured,
      detail: data.maps.configured
        ? data.maps.provider === 'google'
          ? 'Google Maps'
          : 'MapmyIndia'
        : 'Address lookup disabled',
      warn: data.maps.fellBack
        ? `${data.maps.fellBack === 'google' ? 'Google Maps' : 'MapmyIndia'} is selected but has no key, so the other provider is being used.`
        : undefined,
      source: sourceLabel[data.maps.source] ?? data.maps.source,
    },
  ];

  const borrowed = data.sms.dltTemplates.filter((t) => !t.hasOwnTemplate);

  return (
    <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
      <div className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <h2 className="text-ink text-[15px] font-bold">What is live right now</h2>
          <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
            Read from the server. A blank field on the forms below means the value is inherited from
            the server environment — not that it is missing.
          </p>
        </div>
        <Badge tone="neutral">{data.appEnv}</Badge>
      </div>

      <div className="divide-border/60 divide-y">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-wrap items-start gap-3 px-5 py-3">
            <span
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-black text-white ${
                row.ok ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
              aria-hidden
            >
              {row.ok ? '\u2713' : '!'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-ink text-[13.5px] font-bold">{row.label}</p>
              <p className="text-ink-muted text-[12.5px] font-medium">
                {row.detail} <span className="text-ink-muted/70">· {row.source}</span>
              </p>
              {row.warn && (
                <p className="mt-1 text-[12px] font-semibold leading-relaxed text-red-700">
                  {row.warn}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {borrowed.length > 0 && (
        <div className="border-border/60 border-t px-5 py-3">
          <p className="text-ink text-[13px] font-bold">DLT templates</p>
          <p className="text-ink-muted mt-0.5 text-[12.5px] font-medium leading-relaxed">
            {borrowed.map((t) => t.label).join(', ')} {borrowed.length === 1 ? 'has' : 'have'} no
            registered template, so {borrowed.length === 1 ? 'it borrows' : 'they borrow'} the
            approved login template. The code still arrives; the wording says &ldquo;verify your
            login&rdquo;.
          </p>
        </div>
      )}
    </div>
  );
}

export function ApiSettingsPage() {
  const { data: mapmyindiaSetting, isLoading: isLoadingMapmyindia } = useQuery({
    queryKey: ['website-setting', 'mapmyindia_settings'],
    queryFn: () => api.cms.getSetting('mapmyindia_settings').catch(() => null),
  });

  const { data: mapSetting, isLoading: isLoadingMaps } = useQuery({
    queryKey: ['website-setting', 'map_settings'],
    queryFn: () => api.cms.getSetting('map_settings').catch(() => null),
  });

  const { data: cloudinarySetting, isLoading: isLoadingCloudinary } = useQuery({
    queryKey: ['website-setting', 'cloudinary_settings'],
    queryFn: () => api.cms.getSetting('cloudinary_settings').catch(() => null),
  });

  const { data: smsSetting, isLoading: isLoadingSms } = useQuery({
    queryKey: ['website-setting', 'sms_settings'],
    queryFn: () => api.cms.getSetting('sms_settings').catch(() => null),
  });

  const mapmyindiaMutation = useSettingMutation('mapmyindia_settings');
  const mapMutation = useSettingMutation('map_settings');
  const cloudinaryMutation = useSettingMutation('cloudinary_settings');
  const smsMutation = useSettingMutation('sms_settings');

  const mapmyindiaValues = useMemo(
    () =>
      (mapmyindiaSetting?.value as any) || {
        apiKey: '',
        clientId: '',
        clientSecret: '',
      },
    [mapmyindiaSetting?.value],
  );

  const cloudinaryValues = useMemo(
    () =>
      (cloudinarySetting?.value as any) || {
        cloudName: '',
        apiKey: '',
        apiSecret: '',
      },
    [cloudinarySetting?.value],
  );

  // `provider` defaults to the gateway, never to 'console'.
  //
  // There is no `sms_settings` row on a fresh install, so this object is what
  // the form shows — and the form writes back what it shows. Defaulting to
  // 'console' meant an administrator who opened this page and pressed Save,
  // changing nothing, stored `provider: 'console'`. The server reads that
  // stored value before the environment, and 'console' is truthy, so the
  // configured gateway stopped being consulted and every SMS became a log
  // line. Sign-in here is password *plus* an SMS code, so that one click
  // locked every admin and seller out with no way back in from the product.
  //
  // 'bizbrick' with blank credentials is safe in both directions: blank fields
  // fall through to the server's environment values, and if nothing is
  // configured anywhere the server still falls back to the console adapter on
  // its own.
  const smsValues = useMemo(
    () =>
      (smsSetting?.value as any) || {
        provider: 'bizbrick',
        apiUrl: '',
        apiKey: '',
        clientId: '',
        senderId: '',
        entityId: '',
        dltTemplateId: '',
        dltPasswordResetTemplateId: '',
        dltVerifyPhoneTemplateId: '',
        dltVendorNewOrderTemplateId: '',
        dltVendorAcceptReminderTemplateId: '',
        dltVendorOrderCancelledTemplateId: '',
        dltVendorReturnRequestedTemplateId: '',
        dltVendorReturnApprovedTemplateId: '',
        dltVendorReturnArrivedTemplateId: '',
        dltVendorOrderRtoTemplateId: '',
        dltVendorPayoutReleasedTemplateId: '',
        dltVendorPayoutFailedTemplateId: '',
        dltVendorStoreStatusTemplateId: '',
        dltCustomerOrderPlacedTemplateId: '',
        dltCustomerOrderShippedTemplateId: '',
        dltCustomerOrderDeliveredTemplateId: '',
        dltCustomerOrderCancelledTemplateId: '',
      },
    [smsSetting?.value],
  );

  const [mapProvider, setMapProvider] = useState<'mapmyindia' | 'google'>('mapmyindia');
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [mapmyindiaApiKey, setMapmyindiaApiKey] = useState('');
  const [mapmyindiaClientId, setMapmyindiaClientId] = useState('');
  const [mapmyindiaClientSecret, setMapmyindiaClientSecret] = useState('');

  const [cloudinaryCloudName, setCloudinaryCloudName] = useState('');
  const [cloudinaryApiKey, setCloudinaryApiKey] = useState('');
  const [cloudinaryApiSecret, setCloudinaryApiSecret] = useState('');

  const [smsProvider, setSmsProvider] = useState('bizbrick');
  const [indiaHubApiUrl, setIndiaHubApiUrl] = useState('');
  const [indiaHubApiKey, setIndiaHubApiKey] = useState('');
  const [indiaHubSenderId, setIndiaHubSenderId] = useState('');
  const [indiaHubRoute, setIndiaHubRoute] = useState('');
  const [smsApiUrl, setSmsApiUrl] = useState('');
  const [smsApiKey, setSmsApiKey] = useState('');
  const [smsClientId, setSmsClientId] = useState('');
  const [smsSenderId, setSmsSenderId] = useState('');
  const [smsEntityId, setSmsEntityId] = useState('');
  const [smsDltTemplateId, setSmsDltTemplateId] = useState('');
  // One DLT template per purpose — carriers match body text to the registered
  // id, so a login template cannot carry a password-reset message.
  const [smsDltResetTemplateId, setSmsDltResetTemplateId] = useState('');
  const [smsDltVerifyPhoneTemplateId, setSmsDltVerifyPhoneTemplateId] = useState('');
  const [smsDltVendorNewOrder, setSmsDltVendorNewOrder] = useState('');
  const [smsDltVendorAcceptReminder, setSmsDltVendorAcceptReminder] = useState('');
  const [smsDltVendorOrderCancelled, setSmsDltVendorOrderCancelled] = useState('');
  const [smsDltVendorReturnRequested, setSmsDltVendorReturnRequested] = useState('');
  const [smsDltVendorReturnApproved, setSmsDltVendorReturnApproved] = useState('');
  const [smsDltVendorReturnArrived, setSmsDltVendorReturnArrived] = useState('');
  const [smsDltVendorOrderRto, setSmsDltVendorOrderRto] = useState('');
  const [smsDltVendorPayoutReleased, setSmsDltVendorPayoutReleased] = useState('');
  const [smsDltVendorPayoutFailed, setSmsDltVendorPayoutFailed] = useState('');
  const [smsDltVendorStoreStatus, setSmsDltVendorStoreStatus] = useState('');
  const [smsDltCustomerPlaced, setSmsDltCustomerPlaced] = useState('');
  const [smsDltCustomerShipped, setSmsDltCustomerShipped] = useState('');
  const [smsDltCustomerDelivered, setSmsDltCustomerDelivered] = useState('');
  const [smsDltCustomerCancelled, setSmsDltCustomerCancelled] = useState('');

  const [inited, setInited] = useState(false);

  useEffect(() => {
    if ((mapmyindiaSetting || mapSetting || cloudinarySetting || smsSetting) && !inited) {
      const mapValues = (mapSetting?.value as { provider?: string; googleApiKey?: string }) || {};
      setMapProvider(mapValues.provider === 'google' ? 'google' : 'mapmyindia');
      setGoogleMapsApiKey(mapValues.googleApiKey ?? '');
      setMapmyindiaApiKey(mapmyindiaValues.apiKey ?? '');
      setMapmyindiaClientId(mapmyindiaValues.clientId ?? '');
      setMapmyindiaClientSecret(mapmyindiaValues.clientSecret ?? '');

      setCloudinaryCloudName(cloudinaryValues.cloudName ?? '');
      setCloudinaryApiKey(cloudinaryValues.apiKey ?? '');
      setCloudinaryApiSecret(cloudinaryValues.apiSecret ?? '');

      setSmsProvider(smsValues.provider ?? 'bizbrick');
      setIndiaHubApiUrl(smsValues.indiaHubApiUrl ?? '');
      setIndiaHubApiKey(smsValues.indiaHubApiKey ?? '');
      setIndiaHubSenderId(smsValues.indiaHubSenderId ?? '');
      setIndiaHubRoute(smsValues.indiaHubRoute ?? '');
      setSmsApiUrl(smsValues.apiUrl ?? '');
      setSmsApiKey(smsValues.apiKey ?? '');
      setSmsClientId(smsValues.clientId ?? '');
      setSmsSenderId(smsValues.senderId ?? '');
      setSmsEntityId(smsValues.entityId ?? '');
      setSmsDltTemplateId(smsValues.dltTemplateId ?? '');
      setSmsDltResetTemplateId(smsValues.dltPasswordResetTemplateId ?? '');
      setSmsDltVerifyPhoneTemplateId(smsValues.dltVerifyPhoneTemplateId ?? '');
      setSmsDltVendorNewOrder(smsValues.dltVendorNewOrderTemplateId ?? '');
      setSmsDltVendorAcceptReminder(smsValues.dltVendorAcceptReminderTemplateId ?? '');
      setSmsDltVendorOrderCancelled(smsValues.dltVendorOrderCancelledTemplateId ?? '');
      setSmsDltVendorReturnRequested(smsValues.dltVendorReturnRequestedTemplateId ?? '');
      setSmsDltVendorReturnApproved(smsValues.dltVendorReturnApprovedTemplateId ?? '');
      setSmsDltVendorReturnArrived(smsValues.dltVendorReturnArrivedTemplateId ?? '');
      setSmsDltVendorOrderRto(smsValues.dltVendorOrderRtoTemplateId ?? '');
      setSmsDltVendorPayoutReleased(smsValues.dltVendorPayoutReleasedTemplateId ?? '');
      setSmsDltVendorPayoutFailed(smsValues.dltVendorPayoutFailedTemplateId ?? '');
      setSmsDltVendorStoreStatus(smsValues.dltVendorStoreStatusTemplateId ?? '');
      setSmsDltCustomerPlaced(smsValues.dltCustomerOrderPlacedTemplateId ?? '');
      setSmsDltCustomerShipped(smsValues.dltCustomerOrderShippedTemplateId ?? '');
      setSmsDltCustomerDelivered(smsValues.dltCustomerOrderDeliveredTemplateId ?? '');
      setSmsDltCustomerCancelled(smsValues.dltCustomerOrderCancelledTemplateId ?? '');

      setInited(true);
    }
  }, [
    mapmyindiaSetting,
    mapSetting,
    cloudinarySetting,
    smsSetting,
    inited,
    mapmyindiaValues,
    cloudinaryValues,
    smsValues,
  ]);

  const handleSaveMapmyindia = (e: React.FormEvent) => {
    e.preventDefault();
    // Each provider keeps its own row, so switching never wipes the other's keys.
    mapMutation.mutate({ provider: mapProvider, googleApiKey: googleMapsApiKey });
    mapmyindiaMutation.mutate({
      apiKey: mapmyindiaApiKey,
      clientId: mapmyindiaClientId,
      clientSecret: mapmyindiaClientSecret,
    });
  };

  const handleSaveCloudinary = (e: React.FormEvent) => {
    e.preventDefault();
    cloudinaryMutation.mutate({
      cloudName: cloudinaryCloudName,
      apiKey: cloudinaryApiKey,
      apiSecret: cloudinaryApiSecret,
    });
  };

  const handleSaveSms = (e: React.FormEvent) => {
    e.preventDefault();
    smsMutation.mutate({
      provider: smsProvider,
      indiaHubApiUrl,
      indiaHubApiKey,
      indiaHubSenderId,
      indiaHubRoute,
      apiUrl: smsApiUrl,
      apiKey: smsApiKey,
      clientId: smsClientId,
      senderId: smsSenderId,
      entityId: smsEntityId,
      dltTemplateId: smsDltTemplateId,
      dltPasswordResetTemplateId: smsDltResetTemplateId,
      dltVerifyPhoneTemplateId: smsDltVerifyPhoneTemplateId,
      dltVendorNewOrderTemplateId: smsDltVendorNewOrder,
      dltVendorAcceptReminderTemplateId: smsDltVendorAcceptReminder,
      dltVendorOrderCancelledTemplateId: smsDltVendorOrderCancelled,
      dltVendorReturnRequestedTemplateId: smsDltVendorReturnRequested,
      dltVendorReturnApprovedTemplateId: smsDltVendorReturnApproved,
      dltVendorReturnArrivedTemplateId: smsDltVendorReturnArrived,
      dltVendorOrderRtoTemplateId: smsDltVendorOrderRto,
      dltVendorPayoutReleasedTemplateId: smsDltVendorPayoutReleased,
      dltVendorPayoutFailedTemplateId: smsDltVendorPayoutFailed,
      dltVendorStoreStatusTemplateId: smsDltVendorStoreStatus,
      dltCustomerOrderPlacedTemplateId: smsDltCustomerPlaced,
      dltCustomerOrderShippedTemplateId: smsDltCustomerShipped,
      dltCustomerOrderDeliveredTemplateId: smsDltCustomerDelivered,
      dltCustomerOrderCancelledTemplateId: smsDltCustomerCancelled,
    });
  };

  if (isLoadingMapmyindia || isLoadingMaps || isLoadingCloudinary || isLoadingSms) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6 pb-8">
      <EffectiveConfigPanel />

      {/* MapmyIndia Settings */}
      <form onSubmit={handleSaveMapmyindia} className="space-y-6">
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="text-ink text-[15px] font-bold">Maps &amp; Location</h2>
              <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
                Location search, current-location and pincode lookup on the website. Choose the
                provider; each keeps its own keys, so you can switch back any time.
              </p>
            </div>
          </div>
          <div className="space-y-4 p-5">
            <FormSelect
              label="Active map provider"
              value={mapProvider}
              onChange={(e) =>
                setMapProvider(e.target.value === 'google' ? 'google' : 'mapmyindia')
              }
            >
              <option value="mapmyindia">MapmyIndia (Mappls)</option>
              <option value="google">Google Maps</option>
            </FormSelect>
            <p className="text-ink-muted -mt-2 text-[12px] font-medium leading-relaxed">
              If the selected provider has no key, the website keeps using the other one so location
              search never goes dark.
            </p>

            <p className="text-ink pt-2 text-[13px] font-bold">Google Maps</p>
            <Input
              label="Google Maps API Key"
              type="password"
              placeholder={
                (mapSetting?.value as { googleApiKey?: string } | undefined)?.googleApiKey
                  ? '••••••••'
                  : 'AIza…'
              }
              value={googleMapsApiKey}
              onChange={(e) => setGoogleMapsApiKey(e.target.value)}
            />
            <p className="text-ink-muted -mt-2 text-[12px] font-medium leading-relaxed">
              Enable <b>Places API (New)</b> and <b>Geocoding API</b> for this key in Google Cloud.
              It is used only by our server, never sent to browsers — restrict it by the server IP,
              not by website.
            </p>
            <IntegrationTestButton name="google-maps" />

            <p className="text-ink pt-2 text-[13px] font-bold">MapmyIndia</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="API Key"
                placeholder="MapmyIndia public API Key"
                value={mapmyindiaApiKey}
                onChange={(e) => setMapmyindiaApiKey(e.target.value)}
              />
              <Input
                label="Client ID"
                placeholder="OAuth2 Client ID"
                value={mapmyindiaClientId}
                onChange={(e) => setMapmyindiaClientId(e.target.value)}
              />
              <Input
                label="Client Secret"
                type="password"
                placeholder={mapmyindiaValues.clientSecret ? '••••••••' : 'OAuth2 Client Secret'}
                value={mapmyindiaClientSecret}
                onChange={(e) => setMapmyindiaClientSecret(e.target.value)}
              />
            </div>
          </div>
          <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
            <button
              type="submit"
              disabled={mapmyindiaMutation.isPending || mapMutation.isPending}
              className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
            >
              {mapmyindiaMutation.isPending || mapMutation.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Map Settings
            </button>
          </div>
        </div>
      </form>

      {/* Cloudinary Settings */}
      <form onSubmit={handleSaveCloudinary} className="space-y-6">
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="text-ink text-[15px] font-bold">Cloudinary Storage Settings</h2>
              <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
                Configure Cloudinary account credentials for file and image uploads.
              </p>
            </div>
          </div>
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Cloud Name"
                placeholder="Cloudinary Cloud Name"
                value={cloudinaryCloudName}
                onChange={(e) => setCloudinaryCloudName(e.target.value)}
              />
              <Input
                label="API Key"
                placeholder="Cloudinary API Key"
                value={cloudinaryApiKey}
                onChange={(e) => setCloudinaryApiKey(e.target.value)}
              />
              <Input
                label="API Secret"
                type="password"
                placeholder={cloudinaryValues.apiSecret ? '••••••••' : 'Cloudinary API Secret'}
                value={cloudinaryApiSecret}
                onChange={(e) => setCloudinaryApiSecret(e.target.value)}
              />
            </div>
          </div>
          <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
            <button
              type="submit"
              disabled={cloudinaryMutation.isPending}
              className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
            >
              {cloudinaryMutation.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Cloudinary Settings
            </button>
          </div>
        </div>
      </form>

      {/* SMS Gateway Settings */}
      <form onSubmit={handleSaveSms} className="space-y-6">
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="text-ink text-[15px] font-bold">SMS Gateway Configuration</h2>
              <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
                The gateway that sends sign-in codes and order SMS. BizBrick and SMS India Hub each
                keep their own keys; the DLT entity and template IDs below are shared by both.
              </p>
            </div>
          </div>
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <FormSelect
                  label="SMS Provider"
                  value={smsProvider}
                  onChange={(e) => setSmsProvider(e.target.value)}
                >
                  <option value="bizbrick">BizBrick SMS Gateway</option>
                  <option value="smsindiahub">SMS India Hub</option>
                  <option value="console">Console Logger (Development only)</option>
                </FormSelect>
                <p className="text-ink-muted mt-2 text-[12px] font-medium leading-relaxed">
                  Leave a field blank to keep the value configured on the server. Blank does not
                  mean unconfigured — OTPs may already be going out through credentials set in the
                  server environment. Check{' '}
                  <span className="text-ink font-semibold">Notifications → Delivery Dashboard</span>{' '}
                  to see what is actually live.
                </p>
                <p className="mt-1 text-[12px] font-semibold leading-relaxed text-red-700">
                  Do not select Console Logger on a live site: sign-in is password plus an SMS code,
                  so it would lock every admin and seller out.
                </p>
              </div>
              {smsProvider === 'smsindiahub' ? (
                <>
                  <div className="sm:col-span-2">
                    <Input
                      label="SMS India Hub API URL"
                      placeholder="https://cloud.smsindiahub.in/api/mt/SendSMS (default)"
                      value={indiaHubApiUrl}
                      onChange={(e) => setIndiaHubApiUrl(e.target.value)}
                    />
                  </div>
                  <Input
                    label="SMS India Hub API Key"
                    type="password"
                    placeholder={
                      smsValues.indiaHubApiKey ? '••••••••' : 'API Key from SMS India Hub'
                    }
                    value={indiaHubApiKey}
                    onChange={(e) => setIndiaHubApiKey(e.target.value)}
                  />
                  <Input
                    label="Sender ID"
                    placeholder="Approved 6-letter header"
                    value={indiaHubSenderId}
                    onChange={(e) => setIndiaHubSenderId(e.target.value)}
                  />
                  <Input
                    label="Route (optional)"
                    placeholder="Only if SMS India Hub gave you one"
                    value={indiaHubRoute}
                    onChange={(e) => setIndiaHubRoute(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <div className="sm:col-span-2">
                    <Input
                      label="API URL"
                      placeholder="http://65.2.162.85/api/v2/SendSMS"
                      value={smsApiUrl}
                      onChange={(e) => setSmsApiUrl(e.target.value)}
                      disabled={smsProvider !== 'bizbrick'}
                    />
                  </div>
                  <Input
                    label="API Key"
                    type="password"
                    placeholder={smsValues.apiKey ? '••••••••' : 'Gateway API Key'}
                    value={smsApiKey}
                    onChange={(e) => setSmsApiKey(e.target.value)}
                    disabled={smsProvider !== 'bizbrick'}
                  />
                  <Input
                    label="Client ID"
                    placeholder="Gateway Client ID (UUID)"
                    value={smsClientId}
                    onChange={(e) => setSmsClientId(e.target.value)}
                    disabled={smsProvider !== 'bizbrick'}
                  />
                  <Input
                    label="Sender ID"
                    placeholder="e.g. BIZBCK"
                    value={smsSenderId}
                    onChange={(e) => setSmsSenderId(e.target.value)}
                    disabled={smsProvider !== 'bizbrick'}
                  />
                </>
              )}
              <Input
                label="DLT Entity ID"
                placeholder="Principal Entity ID (optional)"
                value={smsEntityId}
                onChange={(e) => setSmsEntityId(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <div className="sm:col-span-2">
                <Input
                  label="DLT Login OTP Template ID"
                  placeholder="e.g. 1007518284266734788"
                  value={smsDltTemplateId}
                  onChange={(e) => setSmsDltTemplateId(e.target.value)}
                  disabled={smsProvider === 'console'}
                />
                <p className="text-ink-muted mt-1.5 text-[12px] font-medium">
                  Carriers drop transactional SMS whose text does not match the registered template,
                  so this ID must point at the approved login OTP copy.
                </p>
              </div>
              <div className="sm:col-span-2">
                <Input
                  label="DLT Password Reset Template ID"
                  placeholder="Paste once BizBrick approves the reset template"
                  value={smsDltResetTemplateId}
                  onChange={(e) => setSmsDltResetTemplateId(e.target.value)}
                  disabled={smsProvider === 'console'}
                />
                <p className="text-ink-muted mt-1.5 text-[12px] font-medium">
                  Password reset needs its own approved template — the login one cannot carry it.
                  Until this is filled in, &ldquo;Forgot password&rdquo; over SMS reports itself
                  unavailable instead of sending messages carriers will drop. Saving here takes
                  effect on the next send; no redeploy needed.
                </p>
              </div>
              <div className="sm:col-span-2">
                <Input
                  label="DLT Phone Verification Template ID"
                  placeholder="Optional — for mobile number verification OTPs"
                  value={smsDltVerifyPhoneTemplateId}
                  onChange={(e) => setSmsDltVerifyPhoneTemplateId(e.target.value)}
                  disabled={smsProvider === 'console'}
                />
              </div>
              <div className="sm:col-span-2">
                <p className="text-ink text-[13px] font-bold">Seller alerts</p>
                <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
                  SMS to sellers for a new order, an order about to be auto-cancelled, a cancelled
                  order, each step of a return, a parcel returned undelivered, payouts and a change
                  to their store&apos;s status. Each needs its own approved template with the exact
                  wording in docs/sms-dlt-template-request.md. A blank field means that alert goes
                  to the seller dashboard only.
                </p>
              </div>
              <Input
                label="DLT Seller New Order Template ID"
                placeholder="Seller new order alert"
                value={smsDltVendorNewOrder}
                onChange={(e) => setSmsDltVendorNewOrder(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Accept Reminder Template ID"
                placeholder="Order waiting for acceptance"
                value={smsDltVendorAcceptReminder}
                onChange={(e) => setSmsDltVendorAcceptReminder(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Order Cancelled Template ID"
                placeholder="Order cancelled — do not ship"
                value={smsDltVendorOrderCancelled}
                onChange={(e) => setSmsDltVendorOrderCancelled(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Return Requested Template ID"
                placeholder="Customer asked for a return"
                value={smsDltVendorReturnRequested}
                onChange={(e) => setSmsDltVendorReturnRequested(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Return Approved Template ID"
                placeholder="Return approved, pickup coming"
                value={smsDltVendorReturnApproved}
                onChange={(e) => setSmsDltVendorReturnApproved(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Return Arrived Template ID"
                placeholder="Return reached the seller, inspect it"
                value={smsDltVendorReturnArrived}
                onChange={(e) => setSmsDltVendorReturnArrived(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Parcel Returned (RTO) Template ID"
                placeholder="Undelivered parcel returned"
                value={smsDltVendorOrderRto}
                onChange={(e) => setSmsDltVendorOrderRto(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Payout Sent Template ID"
                placeholder="Payout sent to the bank"
                value={smsDltVendorPayoutReleased}
                onChange={(e) => setSmsDltVendorPayoutReleased(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Payout Failed Template ID"
                placeholder="Payout bounced back"
                value={smsDltVendorPayoutFailed}
                onChange={(e) => setSmsDltVendorPayoutFailed(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Seller Store Status Template ID"
                placeholder="Store approved, suspended, …"
                value={smsDltVendorStoreStatus}
                onChange={(e) => setSmsDltVendorStoreStatus(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <div className="sm:col-span-2">
                <p className="text-ink text-[13px] font-bold">Customer order updates</p>
                <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
                  SMS to customers when an order is placed, shipped, delivered or cancelled. Same
                  rule: each needs its own approved template, and a blank field means no SMS for
                  that update.
                </p>
              </div>
              <Input
                label="DLT Order Placed Template ID"
                placeholder="Customer order placed"
                value={smsDltCustomerPlaced}
                onChange={(e) => setSmsDltCustomerPlaced(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Order Shipped Template ID"
                placeholder="Customer order shipped"
                value={smsDltCustomerShipped}
                onChange={(e) => setSmsDltCustomerShipped(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Order Delivered Template ID"
                placeholder="Customer order delivered"
                value={smsDltCustomerDelivered}
                onChange={(e) => setSmsDltCustomerDelivered(e.target.value)}
                disabled={smsProvider === 'console'}
              />
              <Input
                label="DLT Order Cancelled Template ID"
                placeholder="Customer order cancelled"
                value={smsDltCustomerCancelled}
                onChange={(e) => setSmsDltCustomerCancelled(e.target.value)}
                disabled={smsProvider === 'console'}
              />
            </div>
          </div>
          <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
            <button
              type="submit"
              disabled={smsMutation.isPending}
              className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
            >
              {smsMutation.isPending ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save SMS Settings
            </button>
          </div>
        </div>
      </form>

      <SettingCard
        settingKey="verification_settings"
        title="GSTIN verification"
        description="Checks a seller's GSTIN against the GST portal at KYC (active status and legal name). Offline checks run either way."
        fields={[
          {
            name: 'gstProvider',
            label: 'Provider',
            options: [
              { value: '', label: 'Server environment' },
              { value: 'none', label: 'None (offline checks only)' },
              { value: 'cashfree', label: 'Cashfree Verification' },
            ],
          },
          { name: 'cashfreeClientId', label: 'Cashfree client id' },
          { name: 'cashfreeClientSecret', label: 'Cashfree client secret', secret: true },
        ]}
      />

      <SettingCard
        settingKey="alert_settings"
        title="Operations alerts"
        description="Where new seller applications are emailed, besides the admins' in-app notifications."
        fields={[
          {
            name: 'opsAlertEmail',
            label: 'Operations email',
            placeholder: 'ops@terrashoppy.com',
            hint: 'Blank uses the server environment.',
          },
        ]}
      />
    </div>
  );
}
